import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Search, Trash2, Pencil, RefreshCcw, X, Bell } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import EmptyState from "@/components/admin/EmptyState";
import ConfirmDialog from "@/components/admin/ConfirmDialog";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

const EMPTY_FORM = { title: "", body: "", audience: "clients" };

export default function AdminNotifications() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { mode: 'create' | 'edit', data }
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.listNotifications(q ? { q } : {});
      setRows(res);
    } catch {
      toast.error("Couldn't load notifications.");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (form) => {
    try {
      if (modal.mode === "create") {
        const created = await adminApi.createNotification(form);
        setRows((prev) => [created, ...prev]);
        toast.success("Notification created.");
      } else {
        const updated = await adminApi.updateNotification(modal.data.id, form);
        setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        toast.success("Notification updated.");
      }
      setModal(null);
    } catch (e) {
      const msg = e?.response?.data?.detail?.[0]?.msg || "Couldn't save.";
      toast.error(typeof msg === "string" ? msg : "Couldn't save.");
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    try {
      await adminApi.deleteNotification(confirm.id);
      setRows((prev) => prev.filter((r) => r.id !== confirm.id));
      toast.success("Deleted.");
    } catch {
      toast.error("Couldn't delete.");
    } finally {
      setConfirm(null);
    }
  };

  return (
    <div data-testid="page-admin-notifications" className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "#6E6759" }} />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title or body…"
            data-testid="notifications-search"
            className="w-full rounded-full border pl-10 pr-4 py-2 text-sm outline-none"
            style={{ background: "#fff", borderColor: "rgba(27,67,50,0.15)", color: "#2A2621" }}
          />
        </div>
        <button type="button" onClick={load} className="btn-outline-forest" data-testid="notifications-refresh">
          <RefreshCcw size={14} /> Refresh
        </button>
        <button
          type="button"
          onClick={() => setModal({ mode: "create", data: EMPTY_FORM })}
          className="btn-gold ml-auto"
          data-testid="notifications-new"
        >
          <Plus size={14} /> New notification
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-3" style={{ color: "#1B4332" }}>
          <Loader2 className="animate-spin" size={16} /> Loading&hellip;
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Bell size={20} />}
          title="No notifications yet"
          subtitle="Draft your first message to send once notifications are wired up."
          testid="notifications-empty"
        />
      ) : (
        <div className="grid gap-4">
          {rows.map((row) => (
            <div
              key={row.id}
              className="rounded-2xl border p-5"
              style={{ background: "#fff", borderColor: "rgba(27,67,50,0.12)" }}
              data-testid={`notification-row-${row.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif text-lg" style={{ color: "#1B4332" }}>{row.title}</span>
                    <span
                      className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full"
                      style={{ background: "#FAF6EC", color: "#1B4332", border: "1px solid rgba(27,67,50,0.15)" }}
                    >
                      {row.audience}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed" style={{ color: "#2A2621" }}>{row.body}</p>
                  <div className="mt-3 text-xs" style={{ color: "#6E6759" }}>{formatDate(row.created_at)}</div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setModal({ mode: "edit", data: row })}
                    className="btn-outline-forest"
                    data-testid={`notification-edit-${row.id}`}
                  >
                    <Pencil size={14} /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirm({ id: row.id, label: row.title })}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border"
                    style={{ borderColor: "#8A2A2A", color: "#8A2A2A" }}
                    data-testid={`notification-delete-${row.id}`}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <NotificationModal
          mode={modal.mode}
          initial={modal.data}
          onClose={() => setModal(null)}
          onSubmit={save}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        title="Delete notification?"
        message={confirm ? `“${confirm.label}” will be removed permanently.` : ""}
        confirmLabel="Delete"
        destructive
        onClose={() => setConfirm(null)}
        onConfirm={doDelete}
        testid="notifications-confirm-delete"
      />
    </div>
  );
}

function NotificationModal({ mode, initial, onClose, onSubmit }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    body: initial?.body || "",
    audience: initial?.audience || "clients",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    setSaving(true);
    await onSubmit(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" data-testid="notification-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative w-full max-w-lg rounded-2xl border p-7"
        style={{ background: "#FAF6EC", borderColor: "rgba(27,67,50,0.15)", boxShadow: "0 20px 60px -20px rgba(27,67,50,0.5)" }}
      >
        <button type="button" onClick={onClose} className="absolute top-4 right-4 inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-black/5" aria-label="Close">
          <X size={14} />
        </button>
        <h3 className="font-serif text-2xl" style={{ color: "#1B4332" }}>
          {mode === "create" ? "New notification" : "Edit notification"}
        </h3>
        <div className="mt-5 space-y-4">
          <label className="flex flex-col gap-2">
            <span className="text-xs tracking-[0.22em] uppercase" style={{ color: "#1B4332" }}>Title</span>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="notification-input-title"
              className="rounded-xl border px-4 py-2 text-sm"
              style={{ background: "#fff", borderColor: "rgba(27,67,50,0.15)" }}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-xs tracking-[0.22em] uppercase" style={{ color: "#1B4332" }}>Body</span>
            <textarea
              rows={5}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="notification-input-body"
              className="rounded-xl border px-4 py-2 text-sm resize-none"
              style={{ background: "#fff", borderColor: "rgba(27,67,50,0.15)" }}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-xs tracking-[0.22em] uppercase" style={{ color: "#1B4332" }}>Audience</span>
            <select
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
              data-testid="notification-input-audience"
              className="rounded-xl border px-4 py-2 text-sm"
              style={{ background: "#fff", borderColor: "rgba(27,67,50,0.15)" }}
            >
              <option value="clients">Clients</option>
              <option value="challenge">7-Day Challenge subscribers</option>
              <option value="corporate">Corporate leads</option>
              <option value="all">Everyone</option>
            </select>
          </label>
        </div>
        <div className="mt-7 flex flex-wrap gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-outline-forest" data-testid="notification-cancel">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-gold" data-testid="notification-save">
            {saving ? "Saving…" : mode === "create" ? "Create" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
